// frontend/src/contexts/AuthContext.js
//
// Clerk-backed auth shim. Preserves the surface that the rest of the app
// expects (useAuth() returning user, profile, loading, authenticated, tier,
// signOut, updateUserProfile, getAccessToken) so consumer components don't
// need to change. Identity comes from Clerk; profile (company_name, tier)
// lives in the Flask backend.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  useUser,
  useAuth as useClerkAuth,
  useClerk,
} from '@clerk/clerk-react';
import getEnvironmentConfig from '../config';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Kept as a no-op export in case any module still imports { supabase }. The
// frontend no longer talks to Supabase directly — all DB access goes through
// the Flask backend, which uses the service role.
export const supabase = null;

export const AuthProvider = ({ children }) => {
  const { isLoaded, isSignedIn, user: clerkUser } = useUser();
  const { getToken, signOut: clerkSignOut } = useClerkAuth();
  const clerk = useClerk();

  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [error, setError] = useState(null);

  const apiBase = useMemo(() => getEnvironmentConfig().apiBaseUrl, []);

  const getAccessToken = useCallback(async () => {
    if (!isSignedIn) return null;
    try {
      return await getToken();
    } catch (err) {
      console.error('getToken failed:', err);
      return null;
    }
  }, [isSignedIn, getToken]);

  const fetchProfile = useCallback(async () => {
    if (!isSignedIn) {
      setProfile(null);
      setProfileLoaded(true);
      return;
    }
    try {
      const token = await getToken();
      if (!token) {
        setProfileLoaded(true);
        return;
      }
      const res = await fetch(`${apiBase}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile || null);
      } else if (res.status === 404) {
        setProfile(null);
      } else {
        console.warn('Profile fetch returned', res.status);
      }
    } catch (err) {
      console.warn('Profile fetch failed:', err);
    } finally {
      setProfileLoaded(true);
    }
  }, [isSignedIn, getToken, apiBase]);

  useEffect(() => {
    if (isLoaded) fetchProfile();
  }, [isLoaded, isSignedIn, fetchProfile]);

  const updateUserProfile = useCallback(async (profileData) => {
    if (!isSignedIn) return { success: false, error: 'Not signed in' };
    try {
      const token = await getToken();
      const res = await fetch(`${apiBase}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setProfile(data.profile || null);
      return { success: true, profile: data.profile };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, [isSignedIn, getToken, apiBase]);

  const signOut = useCallback(async () => {
    try {
      await clerkSignOut();
      setProfile(null);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [clerkSignOut]);

  // Compose a user object that looks enough like the old Supabase shape that
  // existing UI (which reads user.email / user.company_name) keeps working.
  const user = isSignedIn && clerkUser
    ? {
        id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress,
        company_name: profile?.company_name,
      }
    : null;

  const loading = !isLoaded || (isSignedIn && !profileLoaded);

  const value = {
    user,
    profile,
    loading,
    error,
    initializationComplete: isLoaded,
    authenticated: !!isSignedIn,
    // effective_tier is subscription_tier raised by any live paid licence
    // (backend _effective_tier); older API responses only have the latter.
    tier: profile?.effective_tier || profile?.subscription_tier || 'free',
    hasLicences: !!profile?.has_licences,
    refreshProfile: fetchProfile,
    signOut,
    updateUserProfile,
    getAccessToken,
    // Expose Clerk's modal openers for the login UI.
    openSignIn: (opts) => clerk.openSignIn(opts),
    openSignUp: (opts) => clerk.openSignUp(opts),
    // Helpers preserved from the Supabase shim.
    isAuthenticated: () => !!isSignedIn,
    getUserTier: () => profile?.effective_tier || profile?.subscription_tier || 'free',
    getUserEmail: () => clerkUser?.primaryEmailAddress?.emailAddress || null,
    getUserCompany: () => profile?.company_name || null,
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-green-200 border-t-green-500 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Connecting...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
